import { toDegrees, toRadians } from "../Utils";
import { IPlace } from "./Models";
import orderBy from "lodash-es/orderBy.js";
import { FluentIterable } from "fluent-iterable";

import * as L from "leaflet";
import { MapBounds } from "../Kernel";

import { bbox } from "@turf/bbox";
import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import { point, lineString } from "@turf/helpers";
import { bboxPolygon } from "@turf/bbox-polygon";

import { LocationType } from "../../API";

const EARTH_RADIUS_KM = 6371;

export interface ILocation {
    lat: number;
    lon: number;
}

export interface LocationBounds {
    min: ILocation;
    max: ILocation;
}

export type ICircle = {
    location: ILocation;
    radius: number;
};

export function makeLocation(lat: number, lon: number): ILocation {
    return {
        lat,
        lon,
    };
}
export function LatLngToLocation(latLng: L.LatLng) {
    return {
        lat: latLng.lat,
        lon: latLng.lng,
    };
}

export function locationWithinCircle(location: ILocation, circle: ICircle) {
    const distance = distanceInBetweenLocationsMeters(location, circle.location);
    return distance <= circle.radius;
}

export function locationToFixed(location: ILocation, numDecimalPlaces = 6) {
    return {
        lat: parseFloat(location.lat.toFixed(numDecimalPlaces)),
        lon: parseFloat(location.lon.toFixed(numDecimalPlaces)),
    };
}

export function locationToRadians(location: ILocation) {
    return {
        lat: toRadians(location.lat),
        lon: toRadians(location.lon),
    };
}

export function locationToDegrees(location: ILocation) {
    return {
        lat: toDegrees(location.lat),
        lon: toDegrees(location.lon),
    };
}

export function bearing(start: ILocation, dest: ILocation) {
    start = locationToRadians(start);
    dest = locationToRadians(dest);

    const y = Math.sin(dest.lon - start.lon) * Math.cos(dest.lat);
    const x =
        Math.cos(start.lat) * Math.sin(dest.lat) -
        Math.sin(start.lat) * Math.cos(dest.lat) * Math.cos(dest.lon - start.lon);
    let bearing = Math.atan2(y, x);
    bearing = toDegrees(bearing);
    return (bearing + 360) % 360;
}

export function distanceInBetweenLocationsMeters(l1: ILocation, l2: ILocation) {
    const dLat = toRadians(l2.lat - l1.lat);
    const dLon = toRadians(l2.lon - l1.lon);

    const lat1 = toRadians(l1.lat);
    const lat2 = toRadians(l2.lat);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_KM * c * 1000;
}

export function getClosestPlaces(
    places: FluentIterable<IPlace>,
    location: ILocation,
    max: number,
    maxDist: number,
    excludeTypes: LocationType[] = []
) {
    return orderBy(
        [
            ...places
                .map((x) => {
                    return {
                        place: x,
                        dist: distanceInBetweenLocationsMeters(x.data.location, location),
                    };
                })
                .filter((x) => x.dist < maxDist),
        ],
        ["dist"],
        ["asc"]
    )
        .filter((x) => !excludeTypes.includes(x.place.data.spelioData.type as LocationType))
        .slice(0, max);
}

// @function wrapNum(num: Number, range: Number[], includeMax?: Boolean): Number
// Returns the number `num` modulo `range` in such a way so it lies within
// `range[0]` and `range[1]`. The returned value will be always smaller than
// `range[1]` unless `includeMax` is set to `true`.
export function wrapNum(x: number, range: number[], includeMax: boolean) {
    const max = range[1],
        min = range[0],
        d = max - min;
    return x === max && includeMax ? x : ((((x - min) % d) + d) % d) + min;
}

export function normalizeLocation(location: ILocation): ILocation {
    return {
        lon: wrapNum(location.lon, [-180, 180], true),
        lat: wrapNum(location.lat, [-90, 90], true),
    };
}

export function getLocationBoundingBox(location: ILocation, radiusMeters: number): LocationBounds {
    const dY = (360 * (radiusMeters / 1000)) / (2 * Math.PI * EARTH_RADIUS_KM);
    const dX = dY * Math.cos(toRadians(location.lat));
    return {
        min: {
            lat: location.lat - dY,
            lon: location.lon - dX,
        },
        max: {
            lat: location.lat + dY,
            lon: location.lon + dX,
        },
    };
}

export function getPlacesInBoundingBox(
    places: FluentIterable<IPlace>,
    bounds: MapBounds
): IPlace[] {
    const b = bboxPolygon(
        bbox(
            lineString([
                [bounds.sw.lon, bounds.sw.lat],
                [bounds.ne.lon, bounds.ne.lat],
            ])
        )
    );

    const results = [];
    for (const p of places) {
        const location = point([p.data.location.lon, p.data.location.lat]);
        if (booleanPointInPolygon(location, b)) {
            results.push(p);
        }
    }
    return results;
}
